/**
 * Encode sweep actions for Gnosis Safe
 */
import type { Address } from "viem"
import { encodeFunctionData } from "viem"
import { breakoutMarketAbi } from "../../abi/generated/breakoutMarket"
import type { SafeTransaction } from "../../types"

export interface EncodeSweepProtocolFeesParams {
	market: Address
}

/**
 * Encode a sweepProtocolFees transaction for Gnosis Safe
 *
 * Useful for batching fee sweeps across multiple markets.
 *
 * @example
 * ```ts
 * import { encodeSweepProtocolFees, toSafeTransactionBatch } from "@breakout/sdk/actions/encode"
 *
 * const batch = toSafeTransactionBatch({
 *   name: "Weekly Fee Sweep",
 *   transactions: markets.map(market => encodeSweepProtocolFees({ market })),
 * })
 * ```
 */
export function encodeSweepProtocolFees(params: EncodeSweepProtocolFeesParams): SafeTransaction {
	const data = encodeFunctionData({
		abi: breakoutMarketAbi,
		functionName: "sweepProtocolFees",
		args: [],
	})

	return {
		to: params.market,
		value: "0",
		data,
	}
}

export interface EncodeSweepReserveParams {
	market: Address
}

/**
 * Encode a sweepReserve transaction for Gnosis Safe
 *
 * Useful for batching reserve sweeps across multiple finalized markets.
 *
 * @example
 * ```ts
 * import { encodeSweepReserve, toSafeTransactionBatch } from "@breakout/sdk/actions/encode"
 *
 * const batch = toSafeTransactionBatch({
 *   name: "Sweep Finalized Markets",
 *   transactions: finalizedMarkets.map(market => encodeSweepReserve({ market })),
 * })
 * ```
 */
export function encodeSweepReserve(params: EncodeSweepReserveParams): SafeTransaction {
	const data = encodeFunctionData({
		abi: breakoutMarketAbi,
		functionName: "sweepReserve",
		args: [],
	})

	return {
		to: params.market,
		value: "0",
		data,
	}
}
